<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div v-if="location" class="mb-4">
          <a :href="`/businesses/${location.business_id}`" class="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            &larr; Back to {{ location.business_name }}
          </a>
        </div>
        <div v-if="location" class="flex items-start justify-between">
          <div>
            <h1 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {{ location.name }}
            </h1>
            <div class="mt-2 flex flex-wrap gap-2">
              <span
                class="inline-flex rounded-md bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                {{ formatLocationType(location.location_type) }}
              </span>
              <span
                v-if="location.is_primary"
                class="inline-flex rounded-md bg-primary-100 dark:bg-primary-900/40 px-2 py-0.5 text-xs font-medium text-primary-700 dark:text-primary-300">
                Main Location
              </span>
              <span
                class="inline-flex rounded-md px-2 py-0.5 text-xs font-medium"
                :class="statusClass(location.status)">
                {{ formatStatus(location.status) }}
              </span>
            </div>
            <p v-if="location.address" class="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {{ location.address }}
            </p>
          </div>
          <button
            @click="openEditForm"
            class="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
            Edit
          </button>
        </div>
      </div>
    </div>

    <div v-if="location" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="border-b border-gray-200 dark:border-gray-700 mb-8">
        <nav class="flex gap-6 -mb-px">
          <button
            v-for="tab in tabItems"
            :key="tab.id"
            @click="activeTab = tab.id"
            :class="tabButtonClasses(tab)"
            class="py-2 px-1 border-b-2 font-medium text-sm inline-flex items-center gap-2">
            {{ tab.label }}
            <span
              v-if="tab.badge"
              class="rounded-md bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 text-[11px] font-medium text-gray-500 dark:text-gray-300">
              {{ tab.badge }}
            </span>
          </button>
        </nav>
      </div>

      <div
        v-if="activeTabUnavailable"
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">
          {{ activeTabConfig.unavailableTitle }}
        </h3>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {{ activeTabConfig.unavailableMessage }}
        </p>
      </div>

      <AnalyticsTab
        v-else-if="activeTab === 'analytics'"
        :location-id="locationId" />

      <ReviewsTab
        v-else-if="activeTab === 'reviews'"
        :location-id="locationId" />

      <GbpCampaigns
        v-else-if="activeTab === 'gbp_posts'"
        :location-id="locationId"
        :default-keywords="location.target_keywords || []" />

      <LocationNegativeKeywordsTab
        v-else-if="activeTab === 'negative_keywords' && location"
        :location-id="locationId"
        :business-id="location.business_id"
        :account-id="accountId"
        :csrf-token="csrfToken"
        :available-services="availableServices"
        :default-location="defaultLocation" />

      <div v-if="activeTab === 'details'" :class="hasRightColumn ? 'grid grid-cols-1 lg:grid-cols-2 gap-8' : 'max-w-2xl'">
        <div class="space-y-6">
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Setup Readiness
            </h3>
            <dl class="space-y-3">
              <div>
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Status
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ formatStatus(location.status) }}
                </dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Required Details
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ setupReadinessLabel }}
                </dd>
              </div>
              <div v-if="setupReadinessMessage">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Analysis Impact
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ setupReadinessMessage }}
                </dd>
              </div>
            </dl>
          </div>

          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Contact Information
            </h3>
            <dl class="space-y-3">
              <div v-if="location.phone_number">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Phone
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ location.phone_number }}
                </dd>
              </div>
              <div v-if="location.email">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Email
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ location.email }}
                </dd>
              </div>
              <div v-if="location.website_url">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Website
                </dt>
                <dd class="text-sm">
                  <a :href="location.website_url" target="_blank" class="text-primary-600 dark:text-primary-400 hover:underline">{{ location.website_url }}</a>
                </dd>
              </div>
              <div v-if="location.place_id">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Place ID
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100 font-mono text-xs">
                  {{ location.place_id }}
                </dd>
              </div>
            </dl>
          </div>

          <div v-if="location.address_line_1 || location.city" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Address Details
            </h3>
            <dl class="space-y-3">
              <div v-if="location.address_line_1">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Address Line 1
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ location.address_line_1 }}
                </dd>
              </div>
              <div v-if="location.address_line_2">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Address Line 2
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ location.address_line_2 }}
                </dd>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div v-if="location.city">
                  <dt class="text-xs text-gray-500 dark:text-gray-400">
                    City
                  </dt>
                  <dd class="text-sm text-gray-900 dark:text-gray-100">
                    {{ location.city }}
                  </dd>
                </div>
                <div v-if="location.state">
                  <dt class="text-xs text-gray-500 dark:text-gray-400">
                    State
                  </dt>
                  <dd class="text-sm text-gray-900 dark:text-gray-100">
                    {{ location.state }}
                  </dd>
                </div>
                <div v-if="location.zip">
                  <dt class="text-xs text-gray-500 dark:text-gray-400">
                    ZIP
                  </dt>
                  <dd class="text-sm text-gray-900 dark:text-gray-100">
                    {{ location.zip }}
                  </dd>
                </div>
                <div v-if="location.country">
                  <dt class="text-xs text-gray-500 dark:text-gray-400">
                    Country
                  </dt>
                  <dd class="text-sm text-gray-900 dark:text-gray-100">
                    {{ location.country }}
                  </dd>
                </div>
              </div>
            </dl>
          </div>
        </div>

        <div class="space-y-6">
          <div v-if="location.services && location.services.length > 0" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Services
            </h3>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="service in location.services"
                :key="service.name"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200">
                {{ service.name }}
              </span>
            </div>
          </div>

          <div v-if="location.target_keywords && location.target_keywords.length > 0" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Target Keywords
            </h3>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="keyword in location.target_keywords"
                :key="keyword"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                {{ keyword }}
              </span>
            </div>
          </div>

          <div v-if="hasCoordinates" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Coordinates
            </h3>
            <dl class="space-y-2">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <dt class="text-xs text-gray-500 dark:text-gray-400">
                    Latitude
                  </dt>
                  <dd class="text-sm text-gray-900 dark:text-gray-100 font-mono">
                    {{ location.lat || location.cid_lat || location.place_id_lat }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs text-gray-500 dark:text-gray-400">
                    Longitude
                  </dt>
                  <dd class="text-sm text-gray-900 dark:text-gray-100 font-mono">
                    {{ location.lng || location.cid_lng || location.place_id_lng }}
                  </dd>
                </div>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>

    <UiSlidePanel ref="slidePanel" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { hideLoadingSpinner } from '~/javascript/utils/hide_loader.js'
import { getLocation } from '~/javascript/api/locations.js'
import { UiSlidePanel } from '~/javascript/components/ui/slide_panel'
import LocationForm from './form.vue'
import GbpCampaigns from '~/javascript/components/gbp_campaigns/index.vue'
import AnalyticsTab from './analytics_tab.vue'
import ReviewsTab from './reviews_tab.vue'
import LocationNegativeKeywordsTab from '~/javascript/components/negative_keywords/location_negative_keywords_tab.vue'

const props = defineProps({
  locationId: { type: String, required: true },
  accountId: { type: String, default: '' },
  csrfToken: { type: String, default: '' },
  availableServices: { type: Array, default: () => []},
  defaultLocation: { type: String, default: '' }
})

const location = ref(null)
const slidePanel = ref(null)
const activeTab = ref('details')

const hasGoogleLocationReference = computed(() => Boolean(location.value?.remote_id || location.value?.place_id))

const missingRequiredSetupFields = computed(() => {
  if (!location.value) return []
  const requiredFields = location.value.location_type === 'service_area_only' ?
    ['city', 'state'] :
    ['address_line_1', 'city', 'state', 'zip', 'country']

  return requiredFields.filter((field) => !location.value[field])
})

const hasRequiredLocationSetup = computed(() => missingRequiredSetupFields.value.length === 0)

const locationReadyForAnalysis = computed(() => (
  location.value?.status === 'active' && hasRequiredLocationSetup.value
))

const setupReadinessLabel = computed(() => {
  if (!location.value) return ''
  if (location.value.status !== 'active') return 'Excluded from analysis'
  if (!hasRequiredLocationSetup.value) return 'Limited'

  return 'Ready for location analysis'
})

const setupReadinessMessage = computed(() => {
  if (!location.value) return ''
  if (location.value.status !== 'active') {
    return 'Closed, temporarily closed, and unconfirmed locations are not used as the main analysis location.'
  }
  if (!missingRequiredSetupFields.value.length) return ''

  return `Missing ${missingRequiredSetupFields.value.map((field) => field.replace(/_/g, ' ')).join(', ')}.`
})

const tabItems = computed(() => {
  if (!location.value) return []

  return [
    { id: 'details', label: 'Details' },
    {
      id: 'analytics',
      label: 'Analytics',
      badge: location.value.metrics_synced_at ? '' : 'Not synced',
      available: Boolean(location.value.remote_id),
      unavailableTitle: 'Analytics data has not been connected yet',
      unavailableMessage: 'Connect or import the LocalViking/Google Business Profile location identifier before using traffic, search, map, call, or website-click performance signals.'
    },
    {
      id: 'reviews',
      label: 'Reviews',
      badge: location.value.reviews_synced_at ? '' : 'Not synced',
      available: hasGoogleLocationReference.value,
      unavailableTitle: 'Review data has not been connected yet',
      unavailableMessage: 'Add a Place ID or LocalViking location identifier before importing reviews for this location.'
    },
    {
      id: 'gbp_posts',
      label: 'GBP Posts',
      badge: hasGoogleLocationReference.value ? '' : 'Needs Place ID',
      available: location.value.status === 'active' && hasGoogleLocationReference.value,
      unavailableTitle: 'GBP posting is not ready for this location',
      unavailableMessage: 'Use an active location with a confirmed Place ID or connected Google Business Profile location before creating GBP post campaigns.'
    },
    {
      id: 'negative_keywords',
      label: 'Negative Keywords',
      badge: locationReadyForAnalysis.value ? '' : 'Setup needed',
      available: locationReadyForAnalysis.value,
      unavailableTitle: 'Location-specific analysis is not ready yet',
      unavailableMessage: 'Confirm the location is active and complete the required address fields before running location-specific scans or recommendations.'
    }
  ]
})

const activeTabConfig = computed(() => tabItems.value.find((tab) => tab.id === activeTab.value) || {})
const activeTabUnavailable = computed(() => activeTabConfig.value.available === false)

const hasCoordinates = computed(() => {
  if (!location.value) return false
  return location.value.lat || location.value.cid_lat || location.value.place_id_lat
})

const hasRightColumn = computed(() => {
  if (!location.value) return false
  return (location.value.services && location.value.services.length > 0) ||
    (location.value.target_keywords && location.value.target_keywords.length > 0) ||
    hasCoordinates.value
})

function formatLocationType(type) {
  return {
    physical_office: 'Physical Office',
    service_area_only: 'Service Area Only',
    hybrid_location: 'Hybrid Location'
  }[type] || 'Physical Office'
}

function formatStatus(status) {
  return {
    active: 'Active',
    closed: 'Closed',
    temporarily_closed: 'Temporarily Closed',
    needs_client_confirmation: 'Needs Client Confirmation'
  }[status] || 'Active'
}

function statusClass(status) {
  return {
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    closed: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    temporarily_closed: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    needs_client_confirmation: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
  }[status] || 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
}

function tabButtonClasses(tab) {
  if (activeTab.value === tab.id) return 'border-primary-500 text-primary-600 dark:text-primary-400'

  return tab.available === false ?
    'border-transparent text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:border-gray-300' :
    'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'
}

const fetchLocation = async() => {
  try {
    const response = await getLocation(props.locationId)
    location.value = response.data
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching location:', error)
  }
}

const onLocationSaved = () => {
  slidePanel.value.close()
  fetchLocation()
}

const openEditForm = () => {
  slidePanel.value.open({
    component: LocationForm,
    props: { location: location.value, businessId: location.value.business_id },
    events: { saved: onLocationSaved }
  })
}

onMounted(() => {
  hideLoadingSpinner()
  fetchLocation()
})
</script>
